import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IInvoiceNewVo } from '../interfaces/Invoice/iinvoice-new-vo';
import { IInvoiceSageProfileDto } from '../interfaces/Invoice/iinvoice-sage-profile-dto';
import { ISageBalanceForwardDto } from '../interfaces/Sage/isage-balance-forward-dto';
import { ISageCustomerDto } from '../interfaces/Sage/isage-customer-dto';
import { ISageCustomerSearchRequestDto } from '../interfaces/Sage/isage-customer-search-request-dto';
import { IResultObjVo } from '../interfaces/iresult-obj-vo';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private ApiUrl = "";

  constructor(private http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    this.ApiUrl = baseUrl;
  }

  getKrollInvoice(pharmacyId, facility, patientId, startDate, endDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetKrollInvoice/' + pharmacyId + '/' + facility + '/' + patientId + '/' + startDate + '/' + endDate);
  }

  getInvoice(pharmacyId, facility, patientId, startDate, endDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetInvoice/' + pharmacyId + '/' + facility + '/' + patientId + '/' + startDate + '/' + endDate);
  }

  getResidents(pharmacyId, facility, startDate, endDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/getResidents/' + pharmacyId + '/' + facility + '/' + startDate + '/' + endDate);
  }

  getResidentsByMonth(pharmacyId, facility, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/getResidents/' + pharmacyId + '/' + facility + '/' + startDate);
  }

  getInvoicePatient(pharmacyId, facility, startDate, endDate, lastInvoiceNumber): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetInvoicePatient/' + pharmacyId + '/' + facility + '/' + startDate + '/' + endDate + '/' + lastInvoiceNumber);
  }

  public save(invoiceDto, userId): Observable<any> {
    const headers = { 'content-type': 'application/json' };
    const url = this.ApiUrl + 'api/invoice/save/' + userId;
    invoiceDto = {
      ...invoiceDto,
      invoiceReviewStatusId: 3,
      invoiceSageStatusId: 3,
      isDischargeOrDeceased: false,
     };
    const body = JSON.stringify(invoiceDto);

    return this.http.post<any>(url, invoiceDto, { 'headers': headers });
  }

  getInvoices(pharmacyId, facility, patientId, invoiceNumber): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetInvoices/' + pharmacyId + '/' + facility + '/' + patientId + '/' + invoiceNumber);
  }

  deleteInvoice(invoiceId, userId): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/DeleteInvoice/' + invoiceId + '/' + userId);
  }

  // new Invoices
  getNewKrollInvoice(pharmacyId, facility, patientId, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/NewGetKrollInvoice/' + pharmacyId + '/' + facility + '/' + patientId + '/' + startDate);
  }

  getNewPatientInvoice(invoiceId, pharmacyId, facility, patientId, startDate): Observable<any> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetNewPatientInvoice/${invoiceId}/${pharmacyId}/${facility}/${patientId}/${startDate}`);
  }

  getNewInvoices(pharmacyId, facility, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetNewInvoices/' + pharmacyId + '/' + facility + '/' + startDate);
  }

  getNewInvoicesByFacilityWithKroll(pharmacyId, facility, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetNewInvoicesByFacilityWithKroll/' + pharmacyId + '/' + facility + '/' + startDate);
  }

  getNewInvoicesByFacilityWithoutKroll(pharmacyId, facility, startDate): Observable<any> {
    return this.http.get<any>(this.ApiUrl + 'api/invoice/GetNewInvoicesByFacilityWithoutKroll/' + pharmacyId + '/' + facility + '/' + startDate);
  }

  async sendUpdateSage(invoiceId, userId): Promise<any> {
    const url = `${this.ApiUrl}api/invoice/SendUpdateSage/${invoiceId}/${userId}`;
    return await this.http.get<any>(url).toPromise();
  }

  async saveNewInvoice(invoiceDto, userId): Promise<any> {
    const headers = { 'content-type': 'application/json' };
    const url = this.ApiUrl + 'api/invoice/saveorupdate/' + userId;
    const body = JSON.stringify(invoiceDto);

    return await this.http.post<any>(url, invoiceDto, { 'headers': headers }).toPromise();
  }

  async getProfileFromSage(pharmacyId, facility, patientId): Promise<IResultObjVo<string>> {
    const url = `${this.ApiUrl}api/invoice/GetProfileFromSage/${pharmacyId}/${facility}/${patientId}`;
    return await this.http.get<any>(url).toPromise();
  }

  getInvoiceSageProfile(pharmacyId, facility, patientId): Observable<IResultObjVo<IInvoiceSageProfileDto>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetInvoiceSageProfile/${pharmacyId}/${facility}/${patientId}`);
  }

  getPatientBalanceForward(pharmacyId, facility, patientId): Observable<IResultObjVo<ISageBalanceForwardDto>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetPatientBalanceForward/${pharmacyId}/${facility}/${patientId}`);
  }

  searchSageCustomer(request: ISageCustomerSearchRequestDto): Observable<ISageCustomerDto[]> {
    const headers = { 'content-type': 'application/json' };
    return this.http.post<ISageCustomerDto[]>(`${this.ApiUrl}api/invoice/SearchSageCustomer`, request, { headers });
  }

  getInvoicePaymentItemsByYear(pharmacyId, facility, patientId, year): Observable<IResultObjVo<any[]>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetInvoicePaymentItemsByYear/${pharmacyId}/${facility}/${patientId}/${year}`);
  }

  createSalesInvoice(pharmacyId, facilityId, patientId, invoiceId): Observable<IResultObjVo<any>> {
    const header = { 'content-type': 'application/json' };
    const url = `${this.ApiUrl}api/invoice/CreateSalesInvoice/${pharmacyId}/${facilityId}/${patientId}/${invoiceId}`;
    return this.http.post<any>(url, {}, { 'headers': header });
  }

  getPatientInvoiceList(pharmacyId, facility, patientId): Observable<IResultObjVo<IInvoiceNewVo[]>> {
    return this.http.get<any>(`${this.ApiUrl}api/invoice/GetPatientInvoiceList/${pharmacyId}/${facility}/${patientId}`);
  }

  updateSageName(invoiceProfileId: number, pharmacyId: number, facilityId: number, patientId: number, newSageName: string, canSendEmail: Boolean): Observable<any> {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.ApiUrl}api/invoice/UpdateSageName/${invoiceProfileId}/${pharmacyId}/${facilityId}/${patientId}`;

    return this.http.post<any>(url, {
      sageName: newSageName,
      canSendEmail
    }, { 'headers': headers });
  }

  updateSageProfile(invoiceProfileId: number, pharmacyId: number, facilityId: number, patientId: number, profileData: any): Observable<any> {
    const headers = { 'content-type': 'application/json' };
    const url = `${this.ApiUrl}api/invoice/UpdateSageName/${invoiceProfileId}/${pharmacyId}/${facilityId}/${patientId}`;

    return this.http.post<any>(url, profileData, { 'headers': headers });
  }

  sendAllInvoicesByEmail(pharmacyId: number, facilityId :number,  invoiceIds: number[]): Observable<IResultObjVo<string>> {
    const headers = { 'content-type': 'application/json' };
    const url = this.ApiUrl + 'api/invoice/SendAllInvoicesByEmail';
    return this.http.post<IResultObjVo<string>>(url, {
      pharmacyId,
      facilityId,
      invoiceIds,
    }, {
      headers
    });
  }
}
